import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/pos/admin/common/multi-select";
import { posApiFetch } from "@/utils/pos-api";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, ImagePlus, Loader2, X } from "lucide-react";
import { productCategories } from "@/lib/pos/constants";
import ImageCropper, { ImageCropperRef } from "@/components/ui/image-cropper";

// --- Form state ---

interface ProductFormState {
  name: string;
  description: string;
  category: string;
  subcategoryId: string;
  priceUsd: string;
  available: boolean;
  isSignature: boolean;
  isSpicy: boolean;
  isVegan: boolean;
  isRaw: boolean;
  locations: number[];
  menuTypes: number[];
  modifierGroups: number[];
}

const EMPTY_FORM: ProductFormState = {
  name: "",
  description: "",
  category: "",
  subcategoryId: "",
  priceUsd: "",
  available: true,
  isSignature: false,
  isSpicy: false,
  isVegan: false,
  isRaw: false,
  locations: [],
  menuTypes: [],
  modifierGroups: [],
};

// --- Component ---

interface ProductEditorProps {
  productId?: string;
  onBack: () => void;
}

export default function ProductEditor({ productId, onBack }: ProductEditorProps) {
  const isEdit = !!productId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<ProductFormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const cropperRef = useRef<ImageCropperRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Fetch existing product for edit ---

  const { data: existingProduct, isLoading: isLoadingProduct } = useQuery<any>({
    queryKey: ["/v1/pos/products", productId],
    queryFn: async () => {
      const res = await posApiFetch(`/v1/pos/products/${productId}`);
      if (!res.ok) throw new Error("Failed to fetch product");
      return res.json();
    },
    enabled: isEdit,
  });

  // Populate form when product loads
  useEffect(() => {
    if (!existingProduct) return;
    const p = existingProduct;
    if (p.imageUrl) setImagePreviewUrl(p.imageUrl);
    setForm({
      name: p.name ?? "",
      description: p.description ?? "",
      category: p.category ?? "",
      subcategoryId: p.subcategoryId ? String(p.subcategoryId) : (p.subcategory?.id ? String(p.subcategory.id) : ""),
      priceUsd: p.priceUsd ?? "",
      available: p.available ?? true,
      isSignature: !!p.isSignature,
      isSpicy: !!p.isSpicy,
      isVegan: !!p.isVegan,
      isRaw: !!p.isRaw,
      locations: (p.locations ?? []).map((l: any) => (typeof l === "object" ? l.id : l)),
      menuTypes: (p.menuTypes ?? []).map((m: any) => (typeof m === "object" ? m.id : m)),
      modifierGroups: (p.modifierGroups ?? []).map((mg: any) => (typeof mg === "object" ? mg.id : mg)),
    });
  }, [existingProduct]);

  // --- Fetch reference data ---

  const { data: locations = [], isLoading: loadingLocations } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/locations");
      if (!res.ok) return [];
      return res.json();
    },
  });

  const { data: menuTypes = [], isLoading: loadingMenuTypes } = useQuery({
    queryKey: ["/v1/pos/menu-types"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/menu-types");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  const { data: subcategories = [], isLoading: loadingSubcategories } = useQuery({
    queryKey: ["/v1/pos/subcategories"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/subcategories");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  const { data: modifierGroups = [], isLoading: loadingModifierGroups } = useQuery({
    queryKey: ["/v1/pos/modifier-groups"],
    queryFn: async () => {
      const res = await posApiFetch("/v1/pos/modifier-groups");
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : data?.data ?? [];
    },
  });

  // --- Dropdown options ---

  const locationOptions = useMemo(
    () => locations.map((l: any) => ({ value: l.id, label: l.name })),
    [locations]
  );

  const menuTypeOptions = useMemo(
    () => menuTypes.map((m: any) => ({ value: m.id, label: m.name })),
    [menuTypes]
  );

  const subcategoryOptions = useMemo(
    () => subcategories
      .filter((s: any) => s.active !== false)
      .map((s: any) => ({ id: String(s.id), name: s.displayName || s.name })),
    [subcategories]
  );

  const modifierGroupOptions = useMemo(
    () => modifierGroups
      .filter((mg: any) => mg.active !== false)
      .map((mg: any) => ({
        value: mg.id,
        label: `${mg.displayName || mg.name}${mg.required ? " *" : ""}`,
      })),
    [modifierGroups]
  );

  // --- Field updater ---

  const set = <K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // --- Submit ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast({ title: "Validation Error", description: "Product name is required", variant: "destructive" });
      return;
    }
    if (!form.priceUsd || parseFloat(form.priceUsd) < 0) {
      toast({ title: "Validation Error", description: "Price must be 0 or greater", variant: "destructive" });
      return;
    }
    if (!form.category) {
      toast({ title: "Validation Error", description: "Category is required", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      // Backend expects multipart/form-data (multer + normalizeProductMultipart)
      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("description", form.description.trim() || "");
      formData.append("category", form.category);
      if (form.subcategoryId) formData.append("subcategoryId", String(Number(form.subcategoryId)));
      formData.append("priceUsd", form.priceUsd);
      formData.append("available", String(form.available));
      formData.append("isSignature", String(form.isSignature));
      formData.append("isSpicy", String(form.isSpicy));
      formData.append("isVegan", String(form.isVegan));
      formData.append("isRaw", String(form.isRaw));
      formData.append("locations", JSON.stringify(form.locations));
      formData.append("menuTypes", JSON.stringify(form.menuTypes));
      formData.append("modifierGroups", JSON.stringify(form.modifierGroups));
      if (imageFile) formData.append("image", imageFile);

      const url = isEdit ? `/v1/pos/products/${productId}` : "/v1/pos/products";
      const method = isEdit ? "PUT" : "POST";

      const res = await posApiFetch(url, {
        method,
        body: formData,
        headers: {},
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || `Failed to ${isEdit ? "update" : "create"} product`);
      }

      toast({ title: `Product ${isEdit ? "updated" : "created"} successfully` });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/products"] });
      onBack();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err?.message || `Failed to ${isEdit ? "update" : "create"} product`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Loading state ---

  if (isEdit && isLoadingProduct) {
    return (
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to Products
          </Button>
        </div>
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--secondary-text-color)]" />
        </div>
      </div>
    );
  }

  // --- Render ---

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={onBack} className="text-[13px] gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Products
        </Button>
        <h2 className="text-[16px] font-semibold text-[var(--primary-text-color)]">
          {isEdit ? "Edit Product" : "Create Product"}
        </h2>
        <div className="w-[140px]" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 max-w-[900px] mx-auto pb-8">
        {/* Basic Details */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Basic Details</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Name *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Enter product name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Price (USD) *</Label>
                <Input
                  className="h-[36px] text-[13px]"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceUsd}
                  onChange={(e) => set("priceUsd", e.target.value)}
                  placeholder="e.g. 12.99"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Category *</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {productCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Subcategory</Label>
                <Select
                  value={form.subcategoryId}
                  onValueChange={(v) => set("subcategoryId", v)}
                >
                  <SelectTrigger className="h-[36px] text-[13px]">
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoryOptions.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Product Image */}
            <div className="space-y-1">
              <Label className="text-[12px]">Photo</Label>
              <div className="flex items-center gap-4">
                {imagePreviewUrl ? (
                  <div className="relative group">
                    <img
                      src={imagePreviewUrl}
                      alt="Product"
                      className="h-[80px] w-[80px] rounded-md object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => { setImagePreviewUrl(null); setImageFile(null); }}
                      className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-[80px] w-[80px] rounded-md border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-[var(--secondary-text-color)] hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  >
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px]">Upload</span>
                  </button>
                )}
                {imagePreviewUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-[32px] text-[12px]"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && file.size > 2 * 1024 * 1024) {
                    toast({ title: "File too large", description: "Max image size is 2MB", variant: "destructive" });
                  } else if (file) {
                    cropperRef.current?.openWithFile(file);
                  }
                  e.target.value = "";
                }}
              />
              <ImageCropper
                ref={cropperRef}
                initialImage={existingProduct?.imageUrl || undefined}
                aspectRatio={1}
                isPreviewVisible={false}
                onSave={async ({ croppedUrl, fileName }) => {
                  const blob = await (await fetch(croppedUrl)).blob();
                  const file = new File([blob], fileName, { type: blob.type });
                  setImageFile(file);
                  setImagePreviewUrl(croppedUrl);
                }}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-[12px]">Description</Label>
              <Textarea
                className="text-[13px] min-h-[80px]"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                placeholder="Product description"
              />
            </div>

            {/* Availability toggle */}
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={form.available} onCheckedChange={(v) => set("available", v)} />
              <Label className="text-[12px]">Available</Label>
            </div>
          </CardContent>
        </Card>

        {/* Assignments */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Assignments</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[12px]">Menu Types</Label>
                <MultiSelect
                  options={menuTypeOptions}
                  selected={form.menuTypes}
                  onChange={(vals) => set("menuTypes", vals as number[])}
                  placeholder="Select menu types"
                  loading={loadingMenuTypes}
                  isRequired={false}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[12px]">Locations</Label>
                <MultiSelect
                  options={locationOptions}
                  selected={form.locations}
                  onChange={(vals) => set("locations", vals as number[])}
                  placeholder="Select locations"
                  loading={loadingLocations}
                  isRequired={false}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[12px]">Modifier Groups</Label>
              <MultiSelect
                options={modifierGroupOptions}
                selected={form.modifierGroups}
                onChange={(vals) => set("modifierGroups", vals as number[])}
                placeholder="Select modifier groups (e.g., Size, Temperature)"
                loading={loadingModifierGroups}
                isRequired={false}
              />
              <p className="text-[11px] text-[var(--secondary-text-color)]">
                Select which customization options customers can choose when ordering this product
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Dish Type */}
        <Card>
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-[14px] font-semibold">Dish Type</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={form.isSignature} onCheckedChange={(v) => set("isSignature", v)} />
                <Label className="text-[12px]">Signature</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.isSpicy} onCheckedChange={(v) => set("isSpicy", v)} />
                <Label className="text-[12px]">Spicy</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.isVegan} onCheckedChange={(v) => set("isVegan", v)} />
                <Label className="text-[12px]">Vegan</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.isRaw} onCheckedChange={(v) => set("isRaw", v)} />
                <Label className="text-[12px]">Raw</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            className="h-[36px] text-[13px]"
            onClick={onBack}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="h-[36px] text-[13px] bg-[var(--primary-color)] text-white hover:bg-[#9f0e0e]"
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEdit ? "Update Product" : "Create Product"}
          </Button>
        </div>
      </form>
    </div>
  );
}
